import { resolveId } from '@medplum/core'
import { codeFromCodeableConcept } from '@seen/core/codeableConcept'
import { formatDateNumber } from '@seen/core/dates'
import { invariant } from '@seen/core/invariant'
import { ExtendedAllergy } from '@seen/core/models/allergies'
import { Badge, Separator } from '@seen/ui'
import type { JSX } from 'react'
import { useParams } from 'react-router'
import { Activity } from '#src/components/activity/Activity.tsx'
import {
  ALLERGY_STATUS_COLORS,
  ALLERGY_TYPE_LABELS,
  SEVERITY_BADGE_COLORS,
} from '#src/components/clinical/statusConfigs.ts'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { ScrollAreaContent } from '#src/components/core/scroll-area/ScrollAreaContent.tsx'
import { ScrollAreaHeader } from '#src/components/core/scroll-area/ScrollAreaHeader.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { LabelColor } from '#src/components/LabelColor.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'
import { useRead } from '#src/hooks/useRead.ts'
import { AllergyActivity } from '#src/pages/participants/allergies/AllergyActivity.tsx'

export function ParticipantAllergyPage(): JSX.Element {
  const { allergyId } = useParams()
  invariant(allergyId, 'Allergy ID is required')
  return (
    <ParticipantAllergyPageComponent key={allergyId} allergyId={allergyId} />
  )
}

function ParticipantAllergyPageComponent({
  allergyId,
}: {
  allergyId: string
}): JSX.Element {
  const { data: allergy, isLoading } = useRead(ExtendedAllergy, allergyId)

  if (isLoading) {
    return <Loader />
  }

  invariant(allergy, `Allergy not found: ${allergyId}`)

  return (
    <PageLayout contentClassName="mx-auto w-full max-w-[calc(76ch+3rem)]">
      <ScrollAreaHeader className="-mx-4 mt-2 flex items-center justify-between gap-1">
        <h2 className="text-2xl font-semibold">{allergy.name}</h2>
        <MoreDropdownMenu>
          <DropdownMenuMedplumLinkItem to={allergy} />
        </MoreDropdownMenu>
      </ScrollAreaHeader>
      <ScrollAreaContent className="gap-6 p-0">
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-sm">
            {allergy.onsetDateTime && (
              <span>{formatDateNumber(allergy.onsetDateTime)}</span>
            )}
            <ResourceFieldForm<
              ExtendedAllergy,
              'allergy' | 'intolerance' | undefined
            >
              instance={allergy}
              valueFn={() => allergy.type}
              patchFn={(instance, value) => {
                instance.type = value ?? undefined
              }}
              render={({ field: { value, onChange, ...fieldRest } }) => (
                <Combobox
                  {...fieldRest}
                  options={[
                    {
                      label: 'Allergy',
                      value: 'allergy',
                      keywords: ['allergy'],
                    },
                    {
                      label: 'Intolerance',
                      value: 'intolerance',
                      keywords: ['intolerance'],
                    },
                  ]}
                  onSelect={(value) => {
                    onChange(value)
                  }}
                >
                  <Badge className="cursor-pointer" variant="outline">
                    {ALLERGY_TYPE_LABELS[value ?? ''] ?? 'Type'}
                  </Badge>
                </Combobox>
              )}
            />
            <ResourceFieldForm<
              ExtendedAllergy,
              'mild' | 'moderate' | 'severe' | null
            >
              instance={allergy}
              valueFn={() => allergy.severity ?? null}
              patchFn={(instance, value) => {
                instance.severity = value ?? undefined
              }}
              render={({ field: { value, onChange, ...fieldRest } }) => (
                <Combobox
                  {...fieldRest}
                  options={[
                    {
                      label: 'Mild',
                      value: 'mild',
                      keywords: ['mild'],
                    },
                    {
                      label: 'Moderate',
                      value: 'moderate',
                      keywords: ['moderate'],
                    },
                    {
                      label: 'Severe',
                      value: 'severe',
                      keywords: ['severe'],
                    },
                  ]}
                  onSelect={(value) => {
                    onChange(value ?? null)
                  }}
                >
                  <Badge
                    className="cursor-pointer"
                    variant="outline"
                    color={value ? SEVERITY_BADGE_COLORS[value] : undefined}
                  >
                    {value ?? 'Severity'}
                  </Badge>
                </Combobox>
              )}
            />
          </div>
          <ResourceFieldForm<ExtendedAllergy, string>
            instance={allergy}
            valueFn={() =>
              codeFromCodeableConcept(allergy.clinicalStatus) ?? ''
            }
            patchFn={(instance, value) => {
              instance.clinicalStatus = {
                coding: [
                  {
                    system:
                      'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
                    code: value,
                  },
                ],
              }
            }}
            render={({ field: { value, onChange, ...fieldRest } }) => (
              <Combobox
                {...fieldRest}
                options={[
                  {
                    label: 'Active',
                    value: 'active',
                    keywords: ['active'],
                  },
                  {
                    label: 'Inactive',
                    value: 'inactive',
                    keywords: ['inactive'],
                  },
                ]}
                onSelect={(value) => {
                  onChange(value ?? 'active')
                }}
              >
                <RowBadge
                  icon={
                    <LabelColor
                      color={ALLERGY_STATUS_COLORS[value] ?? '#bec2c8'}
                    />
                  }
                  className="cursor-pointer"
                >
                  {value || 'status'}
                </RowBadge>
              </Combobox>
            )}
          />
        </div>
        <ResourceFieldForm<ExtendedAllergy, string>
          debounceTyping
          instance={allergy}
          valueFn={() => allergy.reaction ?? ''}
          patchFn={(instance, value) => {
            instance.reaction = value
          }}
          render={({ field }) => (
            <RichTextEditor
              {...field}
              placeholder="Add reaction details…"
              variant="p"
              simpleText
              contentClassName="p-2 -m-2 w-auto rounded-md"
            />
          )}
        />
        <Separator />
        <Activity
          resource={allergy}
          subject={{
            reference: `Patient/${resolveId(allergy.patient)}`,
          }}
        >
          <AllergyActivity allergy={allergy} />
        </Activity>
      </ScrollAreaContent>
    </PageLayout>
  )
}
